import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  COUNTRIES,
  LANGUAGES,
  SPORTS,
  countryByCode,
  languageByCode,
  sportById,
  type EventStatus,
  type EventSummary,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';
import { Footer, Topbar } from '../components/Chrome.tsx';
import { splitDate, statusLabel, statusTag } from '../lib/format.ts';
import { eventAccent, useAppearance } from '../lib/appearance.ts';

type StatusFilter = 'all' | EventStatus;

const statusFilters: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Alle' },
  { id: 'live', label: 'Jetzt live' },
  { id: 'upcoming', label: 'Kommend' },
  { id: 'finished', label: 'Archiv' },
];

export function Home() {
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sports, setSports] = useState<string[]>([]);
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .listEvents()
        .then((list) => alive && setEvents(list))
        .catch((e: Error) => alive && setError(e.message));
    void load();
    const timer = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const toggleSport = (id: string) =>
    setSports((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  // Nur Sportarten anbieten, zu denen es auch Events gibt.
  const availableSports = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of events) counts.set(e.sport, (counts.get(e.sport) ?? 0) + 1);
    return SPORTS.filter((s) => counts.has(s.id)).map((s) => ({ ...s, count: counts.get(s.id)! }));
  }, [events]);

  const availableCountries = useMemo(
    () => COUNTRIES.filter((c) => events.some((e) => e.country === c.code)),
    [events],
  );
  const availableLanguages = useMemo(
    () => LANGUAGES.filter((l) => events.some((e) => e.language === l.code)),
    [events],
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (status !== 'all' && e.status !== status) return false;
      if (sports.length && !sports.includes(e.sport)) return false;
      if (country && e.country !== country) return false;
      if (language && e.language !== language) return false;
      if (q && !`${e.title} ${e.club} ${e.location} ${e.discipline}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [events, status, sports, country, language, query]);

  const activeFilters = sports.length + (country ? 1 : 0) + (language ? 1 : 0) + (query.trim() ? 1 : 0);
  const resetFilters = () => {
    setSports([]);
    setCountry('');
    setLanguage('');
    setQuery('');
  };

  const liveCount = events.filter((e) => e.status === 'live').length;
  const totalViewers = events.reduce((sum, e) => sum + e.viewers, 0);

  return (
    <div className="app">
      <Topbar />
      <main>
        <section className="hero">
          <div className="shell hero__inner">
            <p className="eyebrow eyebrow--on-dark">
              Vereins-Livestreams ohne Kamerateam
            </p>
            <h1>
              Das Event streamt sich <em>selbst</em>.
            </h1>
            <p>
              Athlet:innen und Zuschauer:innen senden vom Handy, der Verein gibt frei und schneidet daraus einen
              Hauptstream. Ein Chat für alle, eine Regie für den Veranstalter.
            </p>
            <div className="hero__actions">
              <a className="btn btn--lg" href="#events">
                Events ansehen
              </a>
              <Link className="btn btn--lg btn--onstage" to="/events/new">
                Eigenes Event anlegen
              </Link>
            </div>
            <div className="hero__stats">
              <div className="hero__stat">
                <b>{liveCount}</b>
                <span>gerade live</span>
              </div>
              <div className="hero__stat">
                <b>{events.length}</b>
                <span>Events</span>
              </div>
              <div className="hero__stat">
                <b>{totalViewers}</b>
                <span>im Stream</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="events">
          <div className="shell">
            <div className="section__head">
              <div>
                <h2>Events der Vereine</h2>
                <p>
                  Tritt einem Event bei — als Athlet:in mit Startnummer oder als Zuschauer:in. Beide können anfragen,
                  ihre Handykamera in den Hauptstream zu geben.
                </p>
              </div>
              <div className="filters">
                {statusFilters.map((f) => (
                  <button
                    key={f.id}
                    className={status === f.id ? 'is-active' : ''}
                    onClick={() => setStatus(f.id)}
                    type="button"
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="filterbar">
              <div className="filterbar__row">
                <div className="sportchips">
                  {availableSports.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`sportchip${sports.includes(s.id) ? ' is-active' : ''}`}
                      onClick={() => toggleSport(s.id)}
                    >
                      <span aria-hidden>{s.icon}</span>
                      {s.label}
                      <em>{s.count}</em>
                    </button>
                  ))}
                </div>
              </div>

              <div className="filterbar__row filterbar__row--controls">
                <label className="field field--inline">
                  <span>Land</span>
                  <select className="select" value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="">Alle Länder</option>
                    {availableCountries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field field--inline">
                  <span>Sprache</span>
                  <select className="select" value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="">Alle Sprachen</option>
                    {availableLanguages.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field field--inline field--grow">
                  <span>Suche</span>
                  <input
                    className="input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Verein, Ort oder Titel …"
                  />
                </label>

                {activeFilters > 0 && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={resetFilters}>
                    Filter zurücksetzen ({activeFilters})
                  </button>
                )}
              </div>
            </div>

            {error && <div className="notice">API nicht erreichbar: {error}</div>}

            <p className="hint" style={{ margin: '0 0 14px' }}>
              {shown.length} von {events.length} Events
            </p>

            <div className="event-grid">
              {shown.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
            {!error && shown.length === 0 && (
              <div className="empty">
                Keine Events für diese Filter.{' '}
                {activeFilters > 0 && (
                  <button type="button" className="linklike" onClick={resetFilters}>
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="section" style={{ background: 'var(--paper-soft)', borderTop: '1px solid var(--line)' }}>
          <div className="shell">
            <div className="section__head">
              <div>
                <h2>So läuft ein Event</h2>
                <p>Vier Schritte vom Anmelden bis zum Bild im Hauptstream.</p>
              </div>
            </div>
            <div className="steps">
              <div className="step">
                <b>1</b>
                <h4>Verein legt Event an</h4>
                <p>Titel, Sportart, Ort und Startliste — dazu Titelbild und Vereinslogo.</p>
              </div>
              <div className="step">
                <b>2</b>
                <h4>Beitreten</h4>
                <p>Athlet:innen weisen sich über die Startnummer aus, Zuschauer:innen treten direkt bei.</p>
              </div>
              <div className="step">
                <b>3</b>
                <h4>Kamera anfragen</h4>
                <p>Wer senden will, schickt eine Anfrage mit Standort-Label. Der Veranstalter sieht sie sofort.</p>
              </div>
              <div className="step">
                <b>4</b>
                <h4>Regie schaltet</h4>
                <p>Freigegebene Kameras laufen als Vorschau. Ein Klick legt eine davon aufs Hauptbild.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function EventCard({ event }: { event: EventSummary }) {
  const d = splitDate(event.startsAt);
  const sport = sportById(event.sport);
  const country = countryByCode(event.country);
  const { brand } = useAppearance();
  return (
    <article className="event-card" style={{ ['--accent' as string]: eventAccent(event.accent, brand) }}>
      <div className={`event-card__banner${event.coverUrl ? ' has-image' : ''}`}>
        {event.coverUrl && <img className="event-card__cover" src={event.coverUrl} alt="" loading="lazy" />}
        <span className={statusTag[event.status]}>
          {event.status === 'live' && <span className="dot dot--pulse" />}
          {statusLabel[event.status]}
        </span>
        <div className="event-card__date">
          <b>{d.day}</b>
          {d.month} · {d.time}
        </div>
        {event.logoUrl && (
          <img className="event-card__logo" src={event.logoUrl} alt={`Logo ${event.club}`} loading="lazy" />
        )}
      </div>
      <div className="event-card__body">
        <span className="event-card__club">{event.club}</span>
        <h3>{event.title}</h3>
        <div className="event-card__meta">
          <span title={sport.label}>
            {sport.icon} {event.discipline}
          </span>
          <span>
            {country.flag} {event.location}
          </span>
        </div>
        <p className="hint">
          {event.description.slice(0, 105)}
          {event.description.length > 105 ? '…' : ''}
        </p>
        <div className="event-card__foot">
          <div className="event-card__counts">
            <span>{event.participantCount} Starter</span>
            <span>{event.liveSources} Kameras</span>
            <span>{languageByCode(event.language).label}</span>
          </div>
          <Link className="btn btn--sm" to={`/events/${event.slug}`}>
            Ansehen
          </Link>
        </div>
      </div>
    </article>
  );
}
